import {
  Modal,
  IconButton,
  Button,
  ButtonSize,
  ButtonVariant,
  Input,
  InputSize
} from "shared/components";
import { FC, useMemo, useState, useCallback, useRef } from "react";
import { XClose } from "untitledui-js-base";
import InfoIcon from "shared/icons/status-icons/info-icon.svg?react";

import { InformationCircleIcon } from "@hugeicons-pro/core-stroke-rounded";
import { Table } from "../Table";
import {
  CellContext,
  getCoreRowModel,
  useReactTable
} from "@tanstack/react-table";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { instance } from "api/apiBase";
import { ApplicationFeeStatus } from "shared/types";
import { Applicant } from "types/application";
import { getFullName } from "shared/utils/get-full-name";
import moment from "moment/moment";
import { toast } from "react-toastify";
import { getErrorMessage } from "utils/error";
import { Money } from "types/landlord/reports";
import { Loader } from "shared/components/Loader";
import { logger } from "logger";
import type { IconSvgElement } from "@hugeicons/react";

interface Props {
  onClose: () => void;
  applicationId: string;
  onSuccess?: () => void;
  applicantId?: string;
  queryKey?: unknown[];
}

interface RefundAmount {
  applicant_id: string | number;
  refund_amount: Money;
}

interface ApplicantWithRefundAmountInput extends Applicant {
  refundAmountInput: number;
}

const useApplicationMeta = (applicationId: string) => {
  return useQuery({
    queryKey: ["application", applicationId],
    queryFn: () =>
      instance(`/portal/v1/applications/${applicationId}`).then(
        ({ data }) => data
      ),
    enabled: Boolean(applicationId)
  });
};

const useRefundMutation = (
  applicationId: string,
  applicantId?: string,
  invalidateKeys?: unknown[],
  onClose?: () => void
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (
      refundData?:
        | { applicant_refunds: RefundAmount[] }
        | { refund_amount: Money }
    ) => {
      return instance.post(
        applicantId
          ? `/portal/v1/applications/${applicationId}/applicants/${applicantId}/refund`
          : `/portal/v1/applications/${applicationId}/refund`,
        refundData
      );
    },
    onSuccess: () => {
      toast.success("Application successfully refunded");
      if (invalidateKeys?.length)
        queryClient
          .invalidateQueries({ queryKey: invalidateKeys })
          .catch((err) => {
            logger.error("Failed to invalidate queries", err);
          });
      onClose?.();
    },
    onError: (error) => {
      toast.error(`Error refunding applications: ${getErrorMessage(error)}`);
    }
  });
};

export const RefundModal: FC<Props> = ({
  onClose,
  applicationId,
  applicantId,
  queryKey
}) => {
  const applicationMeta = useApplicationMeta(applicationId);
  const { mutate: refund, isPending } = useRefundMutation(
    applicationId,
    applicantId,
    queryKey,
    onClose
  );

  const paidApplicants = useMemo(
    () =>
      (applicationMeta.data?.applicants || []).filter(
        (applicant: Applicant) =>
          applicant.application_fee_status === ApplicationFeeStatus.Paid
      ),
    [applicationMeta.data]
  );

  const applicant = useMemo(
    () =>
      paidApplicants.find(
        (applicant: Applicant) =>
          applicantId && String(applicant.id) === String(applicantId)
      ),
    [paidApplicants, applicantId]
  );

  const inputRefs = useRef<Record<string, HTMLInputElement>>({});

  const [refundAmounts, setRefundAmounts] = useState<Record<string, number>>(
    {}
  );

  const baseTableData = useMemo(() => {
    const applicants = applicant ? [applicant] : paidApplicants;
    const initialRefundAmounts = applicants.reduce(
      (acc, applicant) => {
        acc[String(applicant.id)] =
          applicant.refundable_invoice?.total?.cents || 0;
        return acc;
      },
      {} as Record<string, number>
    );
    setRefundAmounts(initialRefundAmounts);
    return applicants.map((applicant) => ({
      ...applicant,
      refundAmountInput: initialRefundAmounts[String(applicant.id)]
    }));
  }, [applicant, paidApplicants]);

  const tableData = useMemo(() => {
    return baseTableData.map((applicant) => ({
      ...applicant,
      refundAmountInput: refundAmounts[String(applicant.id)]
    }));
  }, [baseTableData, refundAmounts]);

  const handleAmountChange = useCallback(
    (applicantId: string, value: string) => {
      const dollarValue = parseFloat(value) || 0;
      const centsValue = Math.round(dollarValue * 100);
      setRefundAmounts((prev) => ({
        ...prev,
        [applicantId]: centsValue
      }));
    },
    []
  );

  const setInputRef = useCallback(
    (applicantId: string) => (el: HTMLInputElement | null) => {
      if (el) inputRefs.current[applicantId] = el;
    },
    []
  );

  const canRefund = useMemo(() => {
    return tableData.every((applicant) => {
      const applicantId = String(applicant.id);
      const amountCents = refundAmounts[applicantId] || 0;
      const maxAmountCents = applicant.refundable_invoice?.total?.cents || 0;
      return amountCents > 0 && amountCents <= maxAmountCents;
    });
  }, [tableData, refundAmounts]);

  const handleRefund = useCallback(() => {
    if (applicantId) {
      const refund_amount = {
        cents: refundAmounts[applicantId],
        currency: "USD"
      };
      refund({ refund_amount });
    } else {
      const applicant_refunds = Object.entries(refundAmounts).map(
        ([applicantId, amount]) => ({
          applicant_id: applicantId,
          refund_amount: {
            cents: amount,
            currency: "USD"
          }
        })
      );
      refund({ applicant_refunds });
    }
  }, [applicantId, refund, refundAmounts]);

  const columns = useMemo(() => {
    return [
      {
        accessorFn: (data: ApplicantWithRefundAmountInput) =>
          getFullName({
            firstName: data.first_name,
            lastName: data.last_name,
            middleName: data.middle_name,
            short: true
          }),
        header: "Applicant",
        cell: (data: CellContext<ApplicantWithRefundAmountInput, string>) => (
          <p className="text-sm truncate text-content-1000 font-medium">
            {data.getValue()}
          </p>
        )
      },
      {
        accessorKey: "refundable_invoice.created_at",
        header: "Charge date",
        size: 120,
        cell: (data: CellContext<ApplicantWithRefundAmountInput, string>) => (
          <p className="text-sm text-content-400">
            {data.getValue() && moment(data.getValue()).isValid()
              ? moment(data.getValue()).format("MMM DD, YYYY")
              : "--"}
          </p>
        )
      },
      {
        accessorKey: "refundable_invoice.total",
        header: "Amount",
        size: 120,
        cell: (data: CellContext<ApplicantWithRefundAmountInput, Money>) => {
          const applicant = data.row.original;
          const applicantId = String(applicant.id);
          const maxAmountCents = data.getValue().cents || 0;
          const refundAmountInput = applicant.refundAmountInput || 0;

          let error = "";
          if (!refundAmountInput || refundAmountInput <= 0)
            error = "This field is required";
          else if (refundAmountInput > maxAmountCents) error = "Invalid amount";

          return (
            <Input
              id={`refund-amount-${applicantId}`}
              type="money"
              className={error ? "!mb-0.5" : "!mb-0"}
              defaultValue={String(maxAmountCents / 100)}
              ref={setInputRef(applicantId)}
              onChange={(e) => handleAmountChange(applicantId, e.target.value)}
              placeholder="0.00"
              hideOptional
              noIntlNormalization
              error={error}
              HugeErrorIcon={InformationCircleIcon as IconSvgElement}
              size={InputSize.SM}
            />
          );
        }
      }
    ];
  }, []);

  const table = useReactTable<ApplicantWithRefundAmountInput>({
    data: tableData,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getRowId: (row) => String(row.id) // Stable row keys
  });

  return (
    <Modal
      opened={Boolean(applicationId)}
      onClose={onClose}
      panelClassName="max-w-[600px] w-full flex flex-col"
    >
      <div className="p-6 border-b border-alpha-100 flex items-center h-20 justify-between">
        <h1 className="text-xl text-content-1000 font-bold ">Confirm refund</h1>
        <IconButton
          onClick={onClose}
          borderLess
          className="ml-auto translate-x-2"
          Icon={XClose}
        />
      </div>
      <div className="bg-warning-50 grow-0 px-6 py-4 flex gap-4 items-center border-b border-alpha-100">
        <InfoIcon className="text-warning-500 h-5 w-5" />
        <p className="text-sm text-warning-700 font-medium">
          You cannot undo this action. You can only refund a customer one time,
          partially or fully. Once a refund is issued it can take up to 5-7
          business days.
        </p>
      </div>
      <div className="h-full grow-1 overflow-y-auto px-6 py-4 bg-background-900">
        {applicationMeta.isLoading ? (
          <div className="w-full flex items-center justify-center h-full">
            <Loader />
          </div>
        ) : (
          <>
            <p className="text-sm font-medium text-content-500 mb-2.5">
              These items will be refunded
            </p>
            <Table table={table} rowHeight="68px" />
          </>
        )}
      </div>
      <div className="flex gap-2 h-[72px] justify-end px-6 py-4 border-t border-alpha-100">
        <Button
          className="w-[103px]"
          variant={ButtonVariant.BASE}
          onClick={() => onClose()}
          size={ButtonSize.SM}
        >
          Cancel
        </Button>
        <Button
          className="w-[103px]"
          variant={ButtonVariant.PRIMARY}
          onClick={handleRefund}
          loading={isPending}
          size={ButtonSize.SM}
          disabled={applicationMeta.isLoading || isPending || !canRefund}
        >
          Refund
        </Button>
      </div>
    </Modal>
  );
};
